/**
 * @file tonnageAdditions.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-23
 * @modified 2026-10-23
 * @description A split order's tonnage added to the customer's other order of
 *              that day (Cam, 23 Oct 2026 — SO-D10421A, GCB Davis's 1.94 T
 *              split off R J Stitch's SO-D10421, invoiced as part of Davis's
 *              SO-D10545 Gold Label 18 line: one invoice, one line, 7.88 T).
 *              The one set of rules the order screens, Pricing & Costing and
 *              the server share (generated to utils/tonnageAdditions.js by
 *              scripts/sync-shared-utils.js — edit this copy).
 *
 *   NOTHING ELSE CHANGES. The split order, its line, its split link, the PO
 *   line it is on and its supplied quantity stay exactly as they are (the PO
 *   still carries the family, its bill still matches). The target line's own
 *   quantity and supplied quantity stay as they are (its PO-D's supplied must
 *   still match its bill). The addition is a separate figure: the source
 *   line's supplied quantity, in the target line's unit, which the target's
 *   invoice adds to its line. The source order is never invoiced on its own.
 *
 *   - Only a split order (parent_order_id set) — Cam's case, kept narrow.
 *   - The target: the SAME customer's other live order delivered on the SAME
 *     day, not invoiced (no live invoice, a draft included), not archived,
 *     not itself added somewhere, not the split's own family.
 *   - Units: converted through tonnes when both lines' weights are known; a
 *     line of unknown weight, or None (no weight), is refused.
 *   - Undo ("Take it back out") while neither order is invoiced.
 */

import { lineWeightKg, isNoWeight } from './lineUnits';
import { isBulkTonneAccount } from './qtyWithTonnes';
import { formatTonnes, formatCount, roundQuantity } from './numberFormatters';
import { unitLabelFor } from './units';

export const TONNAGE_ADDITIONS_NOT_READY = 'Not ready — migration order_line_additions.sql has not run';
export const ADD_TO_ORDER_LABEL = 'Add to another order…';
export const ADDED_TONNAGE_LABEL = 'Added tonnage…';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const moneyText = (v) => {
  const n = num(v);
  if (n === null) return '—';
  const s = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return `${n < 0 ? '−' : ''}£${s}`;
};

/**
 * The kg one unit of a line counts as: its own weight (0 = None), else the
 * item's, else a tonne on a bulk sales account (utils/tonnage rule 2), else
 * null (unknown — never guessed).
 * @param {{ unit_weight_kg?, item_weight?, item_unit_weight_kg?, sales_account_code?, account_code? }} line
 */
export function kgPerUnit(line = {}) {
  const own = line.unit_weight_kg;
  if (isNoWeight(own)) return 0;
  const kg = lineWeightKg(own, line.item_weight ?? line.item_unit_weight_kg ?? null);
  if (kg) return kg;
  if (isBulkTonneAccount(line.sales_account_code ?? line.account_code)) return 1000;
  return null;
}

/** "7.88 T" or "48 × 25kg bag" — a quantity in a line's unit (kg per unit). */
export function quantityInUnitText(quantity, kg) {
  const q = num(quantity);
  if (q === null) return '—';
  if (kg === 1000) return formatTonnes(q);
  const label = kg ? unitLabelFor(kg) : '';
  return label ? `${formatCount(q)} × ${label}` : formatCount(q);
}

/** The quantity a line is invoiced at on its own: supplied where recorded, else ordered. */
export function ownInvoiceQuantity(line = {}) {
  const s = num(line.supplied_quantity);
  return s !== null ? s : (num(line.quantity) || 0);
}

/**
 * The source line's tonnage in the target line's unit.
 * @returns {{ ok: boolean, quantity: number|null, tonnes: number|null, reason: string|null }}
 */
export function convertToTarget(sourceQty, sourceLine, targetLine) {
  const q = num(sourceQty);
  if (q === null) return { ok: false, quantity: null, tonnes: null, reason: 'No supplied quantity on the split line — record it first' };
  const ks = kgPerUnit(sourceLine);
  const kt = kgPerUnit(targetLine);
  const name = (l) => l?.product_name || l?.product_code || 'the line';
  if (ks === 0) return { ok: false, quantity: null, tonnes: null, reason: `${name(sourceLine)} has no weight (None) — its tonnage cannot be added` };
  if (kt === 0) return { ok: false, quantity: null, tonnes: null, reason: `${name(targetLine)} has no weight (None) — tonnage cannot be added to it` };
  if (ks === null) return { ok: false, quantity: null, tonnes: null, reason: `${name(sourceLine)} has no unit weight — its tonnes are not known` };
  if (kt === null) return { ok: false, quantity: null, tonnes: null, reason: `${name(targetLine)} has no unit weight — its tonnes are not known` };
  const tonnes = roundQuantity((q * ks) / 1000);
  const quantity = ks === kt ? roundQuantity(q) : roundQuantity((q * ks) / kt);
  return { ok: true, quantity, tonnes, reason: null };
}

/**
 * Whether this order may take the split's tonnage, and why not.
 * @param {object} source  the split order { id, account_id, delivery_date, parent_order_id }
 * @param {object} c       a candidate { id, order_number, account_id, delivery_date,
 *        parent_order_id, is_archived, has_live_invoice, draft_invoice_no, added_elsewhere }
 * @returns {string|null}
 */
export function candidateBlock(source, c) {
  if (!source || !c) return 'No order';
  const day = (d) => (d ? String(d).slice(0, 10) : null);
  if (Number(c.id) === Number(source.id)) return 'The split order itself';
  if (Number(c.account_id) !== Number(source.account_id)) return 'Another customer';
  if (!day(c.delivery_date) || day(c.delivery_date) !== day(source.delivery_date)) return 'Delivered on another day';
  const family = [source.id, source.parent_order_id].filter(Boolean).map(Number);
  if (family.includes(Number(c.id)) || (c.parent_order_id && family.includes(Number(c.parent_order_id)))) {
    return 'Part of the split itself';
  }
  if (c.is_archived) return 'Archived';
  if (c.has_live_invoice) return `Invoiced${c.invoice_no ? ` (${c.invoice_no})` : ''}`;
  if (c.draft_invoice_no) return `Draft invoice ${c.draft_invoice_no} waiting in Pricing & Costing`;
  if (c.added_elsewhere) return `Its own tonnage is added to ${c.added_elsewhere}`;
  return null;
}

/**
 * The target line a source line joins by default: the target's only line,
 * else one on the same sales account (most tonnes first), else the line with
 * the most tonnes. Lines it cannot join (no weight known, None) come last.
 * @returns {number|null} the target order line id
 */
export function defaultTargetLine(sourceLine, targetLines = []) {
  const lines = (targetLines || []).filter(Boolean);
  if (!lines.length) return null;
  if (lines.length === 1) return Number(lines[0].id);
  const tonnesOf = (l) => {
    const kg = kgPerUnit(l);
    return kg ? (ownInvoiceQuantity(l) * kg) / 1000 : -1;
  };
  const acct = String(sourceLine?.sales_account_code ?? '').trim().toUpperCase();
  const sorted = [...lines].sort((a, b) => tonnesOf(b) - tonnesOf(a) || Number(a.id) - Number(b.id));
  const same = acct ? sorted.find(l => String(l.sales_account_code ?? '').trim().toUpperCase() === acct && tonnesOf(l) >= 0) : null;
  return Number((same || sorted[0]).id);
}

/**
 * The words the window shows before anything is saved — "SO-D10421A's 1.94 T
 * (Harvester 18) will be invoiced as part of SO-D10545's Gold Label 18 line:
 * 5.94 T + 1.94 T = 7.88 T at £370.00. SO-D10421A stays the split of
 * SO-D10421 (R J Stitch), on PO-D10397, and is not invoiced on its own."
 */
export function additionWords({ source, sourceLine, target, targetLine, quantity, parent = null, poNumber = null }) {
  const kt = kgPerUnit(targetLine);
  const ks = kgPerUnit(sourceLine);
  const own = ownInvoiceQuantity(targetLine);
  const total = roundQuantity(own + (num(quantity) || 0));
  const srcQty = ownInvoiceQuantity(sourceLine);
  const srcName = sourceLine?.product_name || sourceLine?.product_code || 'its product';
  const tgtName = targetLine?.product_name || targetLine?.product_code || 'its';
  const price = num(targetLine?.unit_price);
  const first = `${source.order_number}'s ${quantityInUnitText(srcQty, ks)} (${srcName}) will be invoiced as part of `
    + `${target.order_number}'s ${tgtName} line: ${quantityInUnitText(own, kt)} + ${quantityInUnitText(quantity, kt)} = `
    + `${quantityInUnitText(total, kt)}${price > 0 ? ` at ${moneyText(price)}` : ' (not priced yet)'}.`;
  const splitOf = parent
    ? ` the split of ${parent.order_number}${parent.company_name ? ` (${parent.company_name})` : ''}` : ' a split order';
  const second = `${source.order_number} stays${splitOf}${poNumber ? `, on ${poNumber}` : ''}, and is not invoiced on its own.`;
  return `${first} ${second}`;
}

/**
 * The quantity an order line is invoiced at: its own (supplied, else
 * ordered) plus every live addition into it (each in this line's unit).
 */
export function invoiceQuantity(line = {}, additions = []) {
  const added = (additions || []).reduce((s, a) => s + (num(a.quantity) || 0), 0);
  return roundQuantity(ownInvoiceQuantity(line) + added);
}

/**
 * The cost one unit of the combined line carries: the target's own tonnage
 * at its cost and each addition at its source line's cost, over the whole
 * quantity. `parts`: [{ quantity (target unit), sourceQuantity, sourceCost
 * (per source unit) }]. Null when any cost is not known — a margin on half a
 * cost would mislead.
 */
export function combinedMarginCost(ownCost, ownQty, parts = []) {
  const oc = num(ownCost);
  const oq = num(ownQty) || 0;
  if (oc === null) return null;
  let cost = oc * oq;
  let qty = oq;
  for (const p of parts || []) {
    const sc = num(p.sourceCost);
    if (sc === null) return null;
    cost += sc * (num(p.sourceQuantity) || 0);
    qty += num(p.quantity) || 0;
  }
  if (!(qty > 0)) return oc;
  return Math.round((cost / qty) * 10000) / 10000;
}

/** The pill on the order whose line takes tonnage: "Includes 1.94 T from SO-D10421A". */
export function includesPillText(includes = []) {
  const list = (includes || []).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return `Includes ${formatTonnes(list[0].tonnes)} from ${list[0].source_order_number}`;
  const t = roundQuantity(list.reduce((s, a) => s + (num(a.tonnes) || 0), 0));
  return `Includes ${formatTonnes(t)} from ${list.length} split orders`;
}

/** The pill on the split order whose tonnage went: "Added to SO-D10545". */
export function addedToPillText(addedTo = []) {
  const first = (addedTo || []).find(Boolean);
  return first ? `Added to ${first.target_order_number}` : '';
}

/** "+ 1.94 T from SO-D10421A (split of SO-D10421)" — one addition, on the target's line. */
export function additionLineText(a) {
  if (!a) return '';
  const qty = a.unit_text || formatTonnes(a.tonnes);
  return `+ ${qty} from ${a.source_order_number}${a.parent_order_number ? ` (split of ${a.parent_order_number})` : ''}`;
}

/**
 * The source line's sale side in Pricing & Costing: not a row to price —
 * "Added to SO-D10545 — invoiced there".
 */
export function addedToSaleNote(a) {
  if (!a) return '';
  return `Added to ${a.target_order_number} — invoiced there${a.target_invoice_no ? ` (${a.target_invoice_no})` : ''}`;
}

/** Why the source order cannot be invoiced on its own. */
export function sourceInvoiceBlock(orderNumber, addedTo = []) {
  const first = (addedTo || []).find(Boolean);
  if (!first) return null;
  return `${orderNumber || 'This order'}'s tonnage was added to ${first.target_order_number} — it is invoiced there, not on its own`;
}

/**
 * What the "Add to another order…" action does for an order: { mode, block }.
 * mode 'view' when the order already takes or gave tonnage (the window lists
 * it, with Take it back out); else 'add'.
 * @param {object} order  an order row: id, order_number, parent_order_id | is_split,
 *        is_archived, invoiced_status, draft_invoice_no, has_supplied_qty,
 *        tonnage_additions { added_to: [], includes: [] }, tonnage_candidates
 * @param {{ ready?: boolean, canEdit?: boolean, readOnly?: boolean }} o
 */
export function addToOrderAction(order, { ready = true, canEdit = true, readOnly = false } = {}) {
  if (!order) return { mode: 'add', block: 'No order' };
  const ta = order.tonnage_additions || {};
  const addedTo = (ta.added_to || []).filter(Boolean);
  const includes = (ta.includes || []).filter(Boolean);
  if (!ready) return { mode: 'add', block: TONNAGE_ADDITIONS_NOT_READY };
  if (addedTo.length || includes.length) return { mode: 'view', block: null };
  const isSplit = order.is_split === true || (order.parent_order_id !== null && order.parent_order_id !== undefined && order.parent_order_id !== '');
  if (!isSplit) return { mode: 'add', block: "Only a split order's tonnage can be added to another order" };
  if (readOnly) return { mode: 'add', block: 'View only' };
  if (!canEdit) return { mode: 'add', block: "Adding tonnage to another order needs 'Edit sales orders'" };
  const no = order.order_number || 'This order';
  if (order.is_archived) return { mode: 'add', block: `${no} is archived — restore it first` };
  if (order.invoiced_status === 'Invoiced') return { mode: 'add', block: `${no} is invoiced — its tonnage is on its own invoice` };
  if (order.draft_invoice_no) return { mode: 'add', block: `${no} has a draft invoice ${order.draft_invoice_no} waiting in Pricing & Costing` };
  if (order.has_supplied_qty === false) return { mode: 'add', block: 'Record its supplied quantity first — the tonnage added is what was supplied' };
  if (order.tonnage_candidates !== undefined && order.tonnage_candidates !== null && Number(order.tonnage_candidates) === 0) {
    return { mode: 'add', block: `No other order for this customer delivered on the same day to add it to` };
  }
  return { mode: 'add', block: null };
}

/** Why an addition cannot be taken back out now, or null. */
export function removeBlock(a, { canEdit = true, readOnly = false } = {}) {
  if (!a) return 'Nothing to take out';
  if (readOnly) return 'View only';
  if (!canEdit) return "Needs 'Edit sales orders'";
  if (a.target_invoice_no) return `${a.target_order_number} is invoiced (${a.target_invoice_no}) with the tonnage on it — raise a credit note instead`;
  if (a.target_draft_invoice_no) return `${a.target_order_number} has a draft invoice ${a.target_draft_invoice_no} with the tonnage on it — approve or delete it first`;
  return null;
}
